import React from 'react';
import { Link } from 'react-router-dom';

import Layout from '../components/layout/Layout';

// Export empty object to ensure file is treated as a module
export {};

const Privacy: React.FC = () => {
  return (
    <Layout>
      <div className="bg-gradient-to-br from-dark to-dark-dark text-white py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4 text-primary">Privacy Policy</h1>
            <p className="text-xl max-w-3xl mx-auto">
              How we collect, use, and protect your information on SpinDom.
            </p>
          </div>

          <div className="max-w-4xl mx-auto bg-dark-light rounded-lg shadow-xl overflow-hidden mb-12">
            <div className="p-8">
              <h2 className="text-2xl font-bold mb-6 text-primary">Introduction</h2>
              <p className="text-gray-300 mb-4">Last Updated: April 12, 2025</p>
              <p className="text-gray-300 mb-4">
                Welcome to SpinDom. We respect your privacy and are committed to protecting your
                personal data. This privacy policy will inform you about how we look after your
                personal data when you visit our website and tell you about your privacy rights and
                how the law protects you.
              </p>
              <p className="text-gray-300">
                Please read this privacy policy carefully before using our Service.
              </p>
            </div>
          </div>

          <div className="max-w-4xl mx-auto bg-dark-light rounded-lg shadow-xl overflow-hidden mb-12">
            <div className="p-8">
              <h2 className="text-2xl font-bold mb-6 text-primary">Information We Collect</h2>

              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold mb-2">Personal Data</h3>
                  <p className="text-gray-300 mb-4">
                    While using our Service, we may ask you to provide us with certain personally
                    identifiable information that can be used to contact or identify you. This may
                    include:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                    <li>Email address</li>
                    <li>First name and last name</li>
                    <li>Username</li>
                    <li>Usage data</li>
                    <li>Payment information (processed securely through our payment processors)</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2">Usage Data</h3>
                  <p className="text-gray-300 mb-4">
                    We may also collect information about how the Service is accessed and used. This
                    Usage Data may include:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                    <li>Your computer&apos;s Internet Protocol address (IP address)</li>
                    <li>Browser type and version</li>
                    <li>The pages of our Service that you visit</li>
                    <li>The time and date of your visit</li>
                    <li>The time spent on those pages</li>
                    <li>Unique device identifiers</li>
                    <li>Other diagnostic data</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2">Wheel and Spin Data</h3>
                  <p className="text-gray-300">
                    We collect and store information related to the wheels you create and the spins
                    performed on those wheels. This includes wheel configurations, entries,
                    customization settings, spin results, and timestamps.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto bg-dark-light rounded-lg shadow-xl overflow-hidden mb-12">
            <div className="p-8">
              <h2 className="text-2xl font-bold mb-6 text-primary">How We Use Your Information</h2>

              <div className="space-y-6">
                <p className="text-gray-300 mb-4">
                  We use the information we collect for various purposes, including:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>To provide and maintain our Service</li>
                  <li>To notify you about changes to our Service</li>
                  <li>To allow you to participate in interactive features of our Service</li>
                  <li>To provide customer support</li>
                  <li>To gather analysis or valuable information to improve our Service</li>
                  <li>To monitor the usage of our Service</li>
                  <li>To detect, prevent and address technical issues</li>
                  <li>To process payments and prevent fraudulent transactions</li>
                  <li>To fulfill any other purpose for which you provide it</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto bg-dark-light rounded-lg shadow-xl overflow-hidden mb-12">
            <div className="p-8">
              <h2 className="text-2xl font-bold mb-6 text-primary">Data Security</h2>

              <div className="space-y-6">
                <p className="text-gray-300 mb-4">
                  The security of your data is important to us. We strive to use commercially
                  acceptable means to protect your Personal Data, but remember that no method of
                  transmission over the Internet or method of electronic storage is 100% secure.
                </p>
                <p className="text-gray-300">
                  We implement a variety of security measures to maintain the safety of your
                  personal information when you enter, submit, or access your personal information,
                  including:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    All sensitive information is transmitted using secure socket layer (SSL)
                    technology
                  </li>
                  <li>We use industry-standard encryption for data at rest</li>
                  <li>Access to personal information is restricted to authorized personnel only</li>
                  <li>Regular security assessments and audits</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto bg-dark-light rounded-lg shadow-xl overflow-hidden mb-12">
            <div className="p-8">
              <h2 className="text-2xl font-bold mb-6 text-primary">Your Data Rights</h2>

              <div className="space-y-6">
                <p className="text-gray-300 mb-4">
                  Depending on your location, you may have certain rights regarding your personal
                  data, including:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>The right to access the personal information we hold about you</li>
                  <li>The right to request correction of inaccurate personal information</li>
                  <li>The right to request deletion of your personal information</li>
                  <li>The right to object to processing of your personal information</li>
                  <li>The right to data portability</li>
                  <li>The right to withdraw consent</li>
                </ul>
                <p className="text-gray-300 mt-4">
                  To exercise any of these rights, please contact us using the information provided
                  in the &quot;Contact Us&quot; section below.
                </p>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto bg-dark-light rounded-lg shadow-xl overflow-hidden mb-12">
            <div className="p-8">
              <h2 className="text-2xl font-bold mb-6 text-primary">Cookies and Tracking</h2>

              <div className="space-y-6">
                <p className="text-gray-300 mb-4">
                  We use cookies and similar tracking technologies to track activity on our Service
                  and hold certain information. Cookies are files with a small amount of data which
                  may include an anonymous unique identifier.
                </p>
                <p className="text-gray-300 mb-4">
                  You can instruct your browser to refuse all cookies or to indicate when a cookie
                  is being sent. However, if you do not accept cookies, you may not be able to use
                  some portions of our Service.
                </p>
                <p className="text-gray-300">We use cookies for the following purposes:</p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>To maintain your session and authentication status</li>
                  <li>To remember your preferences and settings</li>
                  <li>To analyze how you use our Service</li>
                  <li>To improve our Service based on your usage patterns</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto bg-dark-light rounded-lg shadow-xl overflow-hidden mb-12">
            <div className="p-8">
              <h2 className="text-2xl font-bold mb-6 text-primary">Third-Party Services</h2>

              <div className="space-y-6">
                <p className="text-gray-300 mb-4">
                  Our Service may contain links to other sites that are not operated by us. If you
                  click on a third-party link, you will be directed to that third party&apos;s site.
                  We strongly advise you to review the Privacy Policy of every site you visit.
                </p>
                <p className="text-gray-300">
                  We have no control over and assume no responsibility for the content, privacy
                  policies, or practices of any third-party sites or services.
                </p>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto bg-dark-light rounded-lg shadow-xl overflow-hidden">
            <div className="p-8">
              <h2 className="text-2xl font-bold mb-6 text-primary">Contact Us</h2>

              <div className="space-y-6">
                <p className="text-gray-300 mb-4">
                  If you have any questions about this Privacy Policy, please contact us:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>By email: privacy@spindom.com</li>
                  <li>
                    By visiting the contact page on our website:{' '}
                    <Link to="/contact" className="text-primary hover:underline">
                      Contact Us
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-400 mb-4">
              By using our Service, you acknowledge that you have read and understand this Privacy
              Policy.
            </p>
            <Link to="/terms" className="btn-secondary px-8 py-3 text-lg">
              View Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Privacy;
